import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, CheckCircle2, ArrowLeft, AlertCircle } from "lucide-react";
import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Die Passwörter stimmen nicht überein");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Das Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await api.post("/api/auth/reset-password", {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("Passwort wurde erfolgreich geändert!");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Zurücksetzen des Passworts";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen login-bg flex items-center justify-center p-4">
        <div className="login-overlay absolute inset-0"></div>
        <Card className="w-full max-w-md relative z-10 shadow-xl border-0">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold">Ungültiger Link</h3>
              <p className="text-muted-foreground text-sm">
                Der Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen.
              </p>
              <Link to="/forgot-password">
                <Button className="w-full mt-4">
                  Neuen Link anfordern
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="login-overlay absolute inset-0"></div>
      
      <Card className="w-full max-w-md relative z-10 shadow-xl border-0 animate-fadeIn">
        <CardHeader className="text-center pb-2">
          <img src="/vytech-logo.png" alt="VyWork" className="h-20 w-auto mx-auto mb-4" />
          <CardTitle className="text-2xl font-bold">
            Neues Passwort festlegen
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Geben Sie Ihr neues Passwort ein.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Passwort geändert!</h3>
              <p className="text-muted-foreground text-sm">
                Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt mit dem neuen Passwort anmelden.
              </p>
              <Button 
                className="w-full mt-4"
                onClick={() => navigate("/login")}
              >
                Zum Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password">Neues Passwort</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-password-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Mindestens 8 Zeichen</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-password-confirm"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-11 btn-primary"
                disabled={loading}
                data-testid="reset-password-submit"
              >
                {loading ? "Wird gespeichert..." : "Passwort speichern"}
              </Button>
              
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück zum Login
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
